import React, { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router-dom'

import { useReactiveVar } from '@apollo/client'
import { addHours, format, setHours, setMinutes } from 'date-fns'
import { Analytics, AppointmentTypeFilter } from 'shared-components'
import { Divider } from 'shared-components'
import { DateSelectionSkeleton } from 'shared-components'
import { RequestAppointmentButton } from 'shared-components'
import { ScrollHelper } from 'shared-components'
import { Subheader } from 'shared-components'

import { Calendar } from '../../components/Calendar'
import { TimePicker } from '../../components/TimePicker'

import {
  alertType,
  appointmentDateTime,
  availabilitySlotIdVar,
  locationData,
  message,
  providerNameVar,
  selectedAppointmentTypeName,
  store,
} from '../../cache'

import './style.css'

export const DatetimeSelect = () => {
  const { locationId, partnerId } = useReactiveVar(store)
  const location = useReactiveVar(locationData)
  const [selectedDay, setSelectedDay] = useState(null)
  const [availableTimes, setAvailableTimes] = useState([])
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null)
  const appointmentTypes = location?.practice?.appointmentTypes ?? []
  const navigate = useNavigate()
  const { t } = useTranslation()
  const appointmentTakenMsg = t(
    'Oops! That appointment time is no longer available. Please choose a new date or time.'
  )

  // Set initial state for the calendar and time picker
  const setInitialState = () => {
    setSelectedDay(null)
    setAvailableTimes([])
  }

  // Set default appointmentType and appointmentTypeName
  useEffect(() => {
    if (appointmentTypes.length > 0 && !selectedAppointmentType) {
      const defaultType = appointmentTypes.find(type => type.isDefault)
      // Fallback to first type if default appointment type is not set
      const appointmentType = defaultType || appointmentTypes[0]
      onAppointmentTypeSelected(appointmentType.name)
    }
  }, [appointmentTypes, selectedAppointmentType])

  const onAppointmentTypeSelected = appointmentTypeName => {
    const appointmentType = appointmentTypes.find(
      type => type.name === appointmentTypeName
    )
    setSelectedAppointmentType(appointmentType)
    selectedAppointmentTypeName(appointmentType.name)
    setInitialState()
  }

  const onDaySelected = (day, availability = { times: [] }) => {
    if (day == null || day == undefined) {
      setInitialState()
    } else {
      setSelectedDay(new Date(day))
      setAvailableTimes(availability.times)
      Analytics.dateSelected({
        locationId,
        partnerId,
        dayOfWeek: format(day, 'EEEE'),
        month: format(day, 'MMMM'),
      })
    }
  }

  const onTimeSelected = timeSlot => {
    const [hours, minutes] = timeSlot.startTime.split(':')
    const datetime = setMinutes(setHours(selectedDay, hours), minutes)

    if (isTimeInThePast(datetime)) {
      alertType('warning')
      message(appointmentTakenMsg)
      navigate('/')
      return
    }

    appointmentDateTime(datetime)
    availabilitySlotIdVar(timeSlot.id)
    providerNameVar(timeSlot.provider)
    navigate('/book-appointment')
    Analytics.timeSelected({ locationId, partnerId, time: timeSlot.startTime })
  }

  const isTimeInThePast = datetime => {
    const now = addHours(new Date(), 1)
    return datetime <= now
  }

  return (
    <div className="datetime-select">
      <div className="body">
        <Suspense fallback={<DateSelectionSkeleton />}>
          {appointmentTypes.length > 0 ? (
            <div className="treatment-selection">
              <AppointmentTypeFilter
                location={location}
                appointmentTypes={appointmentTypes}
                onAppointmentTypeSelected={onAppointmentTypeSelected}
                selectedAppointmentType={selectedAppointmentType}
              />
            </div>
          ) : null}
          <div className="date-selection">
            <Subheader text={t('Select a date')} />
            <Calendar
              onDaySelected={onDaySelected}
              selectedDay={selectedDay}
              navigate={navigate}
              selectedAppointmentType={selectedAppointmentType}
            />
          </div>
          {availableTimes.length > 0 ? (
            <div className="time-selection">
              <Subheader text={t('Select a time')} />
              <TimePicker
                onTimeSelected={onTimeSelected}
                availableTimes={availableTimes}
              />
            </div>
          ) : null}
        </Suspense>
        <Divider variant="secondary" />
        <RequestAppointmentButton />
        <ScrollHelper />
      </div>
    </div>
  )
}
